import { defineComponent, createBlock, openBlock, Teleport, createVNode, normalizeProps, guardReactiveProps, unref, withCtx, renderSlot, computed, useAttrs, inject, ref, toRef, watch, onMounted, withDirectives, createCommentVNode, onBeforeUnmount, createElementBlock, withModifiers, withKeys, normalizeStyle, normalizeClass, Transition, mergeProps, toHandlers, createElementVNode, vShow, nextTick, reactive } from 'vue';
import { useEventListener } from './basler-components.es378.js';
import { useFocusTrap } from './basler-components.es407.js';

const ue = {
  /**
   * @description An uniq name for the open/close a modal via vfm.open/vfm.close APIs.
   * @default `undefined`
   * @example Symbol: `Symbol('MyModal')`
   * @example String: `'AUniqString'`
   * @example Number: `300`
   */
  modalId: {
    type: [String, Number, Symbol],
    default: void 0
  },
  /**
   * @description Display the modal or not.
   * @default `undefined`
   * @example
   * ```js
   * const showModal = ref(false)
   * v-model="showModal"
   * ```
   */
  modelValue: {
    type: Boolean,
    default: void 0
  },
  /**
   * @description Render the modal via `if` or `show`.
   * @default `'if'`
   * @example
   * ```js
   * displayDirective: 'if'
   * ```
   * @example
   * ```js
   * displayDirective: 'show'
   * ```
   */
  displayDirective: {
    type: String,
    default: "if",
    validator: (e) => ["if", "show"].includes(e)
  },
  /**
   * @description Hide the overlay or not.
   * @default `undefined`
   * @example
   * ```js
   * hideOverlay="true"
   * ```
   */
  hideOverlay: {
    type: Boolean,
    default: void 0
  },
  /**
   * @description Customize the overlay transition.
   * @default `undefined`
   */
  overlayTransition: {
    type: [String, Object],
    default: void 0
  },
  /**
   * @description Customize the content transition.
   * @default `undefined`
   */
  contentTransition: {
    type: [String, Object],
    default: void 0
  },
  /**
   * @description Bind class to vfm__overlay.
   * @default `undefined`
   */
  overlayClass: {
    type: void 0,
    default: void 0
  },
  /**
   * @description Bind class to vfm__content.
   * @default `undefined`
   */
  contentClass: {
    type: void 0,
    default: void 0
  },
  /**
   * @description Bind style to vfm__overlay.
   * @default `undefined`
   */
  overlayStyle: {
    type: [String, Object, Array],
    default: void 0
  },
  /**
   * @description Bind style to vfm__content.
   * @default `undefined`
   */
  contentStyle: {
    type: [String, Object, Array],
    default: void 0
  },
  /**
   * @description Is it allow to close the modal by clicking the overlay.
   * @default `true`
   */
  clickToClose: {
    type: Boolean,
    default: true
  },
  /**
   * @description Is it allow to close the modal by keypress `esc`.
   * @default `true`
   */
  escToClose: {
    type: Boolean,
    default: true
  },
  /**
   * @description Is it allow to click outside of the vfm__content when the modal is opened
   * @default `'non-interactive'`
   */
  background: {
    type: String,
    default: "non-interactive",
    validator: (e) => ["interactive", "non-interactive"].includes(e)
  },
  /**
   * @description
   * * Use `{ disabled: true }` to disable the focusTrap.
   * * Checkout the createOptions type here https://github.com/focus-trap/focus-trap for more.
   * @default `{ allowOutsideClick: true }`
   */
  focusTrap: {
    type: [Boolean, Object],
    default: () => ({
      allowOutsideClick: true
    })
  },
  /**
   * @description Lock body scroll or not when the modal is opened.
   * @default `true`
   */
  lockScroll: {
    type: Boolean,
    default: true
  },
  /**
   * @description Define how to increase the zIndex when there are nested modals
   * @default `({ index }) => 1000 + 2 * index`
   */
  zIndexFn: {
    type: Function,
    default: ({ index: e }) => 1e3 + 2 * e
  },
  /**
   * @description The direction of swiping to close the modal
   * @default `none`
   * @example
   * Set swipeToClose="none" to disable swiping to close
   * ```js
   * swipeToClose="none"
   * ```
   */
  swipeToClose: {
    type: String,
    default: "none",
    validator: (e) => ["none", "up", "right", "down", "left"].includes(e)
  },
  /**
   * @description Threshold for swipe to close
   * @default `0`
   */
  threshold: {
    type: Number,
    default: 0
  },
  /**
   * @description If set `:showSwipeBanner="true"`, only allow clicking `swipe-banner` slot to swipe to close
   * @default `undefined`
   * @example
   * ```js
   * swipeToClose="right"
   * :showSwipeBanner="true"
   * ```
   * ```html
   * <VueFinalModal
   *   ...
   *   swipeToClose="right"
   *   :showSwipeBanner="true"
   * >
   *   <template #swipe-banner>
   *     <div style="position: absolute; height: 100%; top: 0; left: 0; width: 10px;" />
   *   </template>
   *   ...modal content
   * </VueFinalModal>
   * ```
   */
  showSwipeBanner: {
    type: Boolean,
    default: void 0
  },
  /**
   * @description When set `:preventNavigationGestures="true"`, there will be two invisible bars for prevent navigation gestures including swiping back/forward on mobile webkit. For example: Safari mobile.
   * @default `undefined`
   * @example
   * Set preventNavigationGestures="true" to prevent Safari navigation gestures including swiping back/forward.
   * ```js
   * :preventNavigationGestures="true"
   * ```
   */
  preventNavigationGestures: {
    type: Boolean,
    default: void 0
  }
};
function ge(e = false) {
  const o = ref(e), n = ref(o.value ? 0 : void 0);
  return [o, n, {
    beforeEnter() {
      n.value = 1;
    },
    afterEnter() {
      n.value = 0;
    },
    beforeLeave() {
      n.value = 3;
    },
    afterLeave() {
      n.value = 2;
    }
  }];
}
function so(e, o) {
  const { modelValueLocal: n, onEntering: l, onEnter: u, onLeaving: i, onLeave: s } = o, t = ref(n.value), [r, f, y] = ge(t.value), [d, c, v] = ge(t.value), C = computed(() => typeof e.contentTransition == "string" ? { name: e.contentTransition } : { ...e.contentTransition }), B = computed(() => typeof e.overlayTransition == "string" ? { name: e.overlayTransition } : { ...e.overlayTransition }), g = computed(
    () => (e.hideOverlay || c.value === 2) && f.value === 2
    /* Leave */
  );
  watch(
    g,
    (x) => {
      x && (t.value = false);
    }
  ), watch(f, (x) => {
    switch (x) {
      case 1:
        return l == null ? void 0 : l();
      case 0:
        return u == null ? void 0 : u();
      case 3:
        return i == null ? void 0 : i();
      case 2:
        return s == null ? void 0 : s();
    }
  });
  async function M() {
    t.value = true, await nextTick(), r.value = true, d.value = true;
  }
  function P() {
    r.value = false, d.value = false;
  }
  return {
    visible: t,
    contentVisible: r,
    contentListeners: y,
    contentTransition: C,
    overlayVisible: d,
    overlayListeners: v,
    overlayTransition: B,
    enterTransition: M,
    leaveTransition: P
  };
}
function ro(e, o, n) {
  const { vfmRootEl: l, vfmContentEl: u, visible: i, modelValueLocal: s } = n, t = ref();
  function r() {
    i.value && e.escToClose && (s.value = false);
  }
  function f(d) {
    t.value = d == null ? void 0 : d.target;
  }
  function y() {
    var d;
    t.value === l.value && (e.clickToClose ? s.value = false : ((d = u.value) == null || d.focus(), o("clickOutside")));
  }
  return {
    onEsc: r,
    onMouseupRoot: y,
    onMousedown: f
  };
}
function ao(e, o) {
  const n = ref(!!e.modelValue);
  return watch(() => e.modelValue, (l) => {
    n.value = !!l;
  }), watch(n, (l) => {
    l !== e.modelValue && o("update:modelValue", l);
  }), {
    modelValueLocal: n
  };
}
function io(e, o) {
  if (e.focusTrap === false)
    return {
      focus() {
      },
      blur() {
      }
    };
  const { focusEl: n } = o, { hasFocus: l, activate: u, deactivate: i } = useFocusTrap(n, e.focusTrap);
  function s() {
    requestAnimationFrame(() => {
      u();
    });
  }
  function t() {
    l.value && i();
  }
  return { focus: s, blur: t };
}
let de = false;
if (typeof window < "u") {
  const e = {
    get passive() {
      de = true;
    }
  };
  window.addEventListener("testPassive", null, e), window.removeEventListener("testPassive", null, e);
}
const De = typeof window < "u" && window.navigator && window.navigator.platform && (/iP(ad|hone|od)/.test(window.navigator.platform) || window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
let H = [], ee = false, _ = 0, Fe = -1, U, W;
const uo = (e) => {
  if (!e || e.nodeType !== Node.ELEMENT_NODE)
    return false;
  const o = window.getComputedStyle(e);
  return ["auto", "scroll"].includes(o.overflowY) && e.scrollHeight > e.clientHeight;
}, co = (e, o) => !(e.scrollTop === 0 && o < 0 || e.scrollTop + e.clientHeight + o >= e.scrollHeight && o > 0), fo = (e) => {
  const o = [];
  for (; e; ) {
    if (o.push(e), e.classList.contains("vfm"))
      return o;
    e = e.parentElement;
  }
  return o;
}, vo = (e, o) => {
  let n = false;
  return fo(e).forEach((u) => {
    uo(u) && co(u, o) && (n = true);
  }), n;
}, Ae = (e) => H.some(() => vo(e, -_)), oe = (e) => {
  const o = e || window.event;
  return Ae(o.target) || o.touches.length > 1 ? true : (o.preventDefault && o.preventDefault(), false);
}, po = (e) => {
  if (W === void 0) {
    const o = !!e && e.reserveScrollBarGap === true, n = window.innerWidth - document.documentElement.clientWidth;
    if (o && n > 0) {
      const l = parseInt(getComputedStyle(document.body).getPropertyValue("padding-right"), 10);
      W = document.body.style.paddingRight, document.body.style.paddingRight = `${l + n}px`;
    }
  }
  U === void 0 && (U = document.body.style.overflow, document.body.style.overflow = "hidden");
}, mo = () => {
  W !== void 0 && (document.body.style.paddingRight = W, W = void 0), U !== void 0 && (document.body.style.overflow = U, U = void 0);
}, yo = (e) => e ? e.scrollHeight - e.scrollTop <= e.clientHeight : false, ho = (e, o) => (_ = e.targetTouches[0].clientY - Fe, Ae(e.target) ? false : o && o.scrollTop === 0 && _ > 0 || yo(o) && _ < 0 ? oe(e) : (e.stopPropagation(), true)), wo = (e, o) => {
  if (!e) {
    console.error(
      "disableBodyScroll unsuccessful - targetElement must be provided when calling disableBodyScroll on IOS devices."
    );
    return;
  }
  if (H.some((l) => l.targetElement === e))
    return;
  const n = {
    targetElement: e,
    options: o || {}
  };
  H = [...H, n], De ? (e.ontouchstart = (l) => {
    l.targetTouches.length === 1 && (Fe = l.targetTouches[0].clientY);
  }, e.ontouchmove = (l) => {
    l.targetTouches.length === 1 && ho(l, e);
  }, ee || (document.addEventListener("touchmove", oe, de ? { passive: false } : void 0), ee = true)) : po(o);
}, bo = (e) => {
  if (!e) {
    console.error(
      "enableBodyScroll unsuccessful - targetElement must be provided when calling enableBodyScroll on IOS devices."
    );
    return;
  }
  H = H.filter((o) => o.targetElement !== e), De ? (e.ontouchstart = null, e.ontouchmove = null, ee && H.length === 0 && (document.removeEventListener("touchmove", oe, de ? { passive: false } : void 0), ee = false)) : H.length || mo();
};
function To(e, o) {
  const { lockScrollEl: n } = o;
  watch(() => e.lockScroll, (i) => {
    i ? u() : l();
  }), onBeforeUnmount(() => {
    l();
  });
  function l() {
    n.value && bo(n.value);
  }
  function u() {
    e.lockScroll && n.value && wo(n.value, {
      reserveScrollBarGap: true,
      allowTouchMove: (i) => {
        for (; i && i !== document.body; ) {
          if (i.getAttribute("vfm-scroll-lock-ignore") !== null)
            return true;
          i = i.parentElement;
        }
        return false;
      }
    });
  }
  return {
    enableBodyScroll: l,
    disableBodyScroll: u
  };
}
function Mo(e) {
  function o(n) {
    switch (n) {
      case "beforeOpen":
        e(n);
        break;
      case "beforeClose":
        e(n);
        break;
      case "opened":
        e(n);
        break;
      case "closed":
        e(n);
        break;
    }
  }
  return {
    emitEvent: o
  };
}
function So(e) {
  const o = ref();
  function n(u) {
    var i;
    o.value = (i = e.zIndexFn) == null ? void 0 : i.call(e, { index: u <= -1 ? 0 : u });
  }
  function l() {
    o.value = void 0;
  }
  return {
    zIndex: o,
    refreshZIndex: n,
    resetZIndex: l
  };
}
const Oo = (e) => (...o) => {
  e && (e == null || e(...o), e = null);
}, D = () => {
};
function K(e, o, n) {
  return e > n ? n : e < o ? o : e;
}
const Ee = (e) => {
  if (e instanceof MouseEvent) {
    const { clientX: o, clientY: n } = e;
    return { x: o, y: n };
  } else {
    const { clientX: o, clientY: n } = e.targetTouches[0];
    return { x: o, y: n };
  }
};
function Co(e) {
  if (!e)
    return false;
  let o = false;
  const n = {
    get passive() {
      return o = true, false;
    }
  };
  return e.addEventListener("x", D, n), e.removeEventListener("x", D), o;
}
function go(e, {
  threshold: o = 0,
  onSwipeStart: n,
  onSwipe: l,
  onSwipeEnd: u,
  passive: i = true
}) {
  const s = reactive({ x: 0, y: 0 }), t = reactive({ x: 0, y: 0 }), r = computed(() => s.x - t.x), f = computed(() => s.y - t.y), { max: y, abs: d } = Math, c = computed(
    () => y(d(r.value), d(f.value)) >= o
  ), v = ref(false), C = computed(() => c.value ? d(r.value) > d(f.value) ? r.value > 0 ? "left" : "right" : f.value > 0 ? "up" : "down" : "none"), B = (p, w) => {
    s.x = p, s.y = w;
  }, g = (p, w) => {
    t.x = p, t.y = w;
  };
  let M, P;
  function x(p) {
    M.capture && !M.passive && p.preventDefault();
    const { x: w, y: A } = Ee(p);
    B(w, A), g(w, A), n == null || n(p), P = [
      useEventListener(e, "mousemove", E, M),
      useEventListener(e, "touchmove", E, M),
      useEventListener(e, "mouseup", a, M),
      useEventListener(e, "touchend", a, M),
      useEventListener(e, "touchcancel", a, M)
    ];
  }
  function E(p) {
    const { x: w, y: A } = Ee(p);
    g(w, A), !v.value && c.value && (v.value = true), v.value && (l == null || l(p));
  }
  function a(p) {
    v.value && (u == null || u(p, C.value)), v.value = false, P.forEach((w) => w());
  }
  let m = [];
  return onMounted(() => {
    const p = Co(window == null ? void 0 : window.document);
    i ? M = p ? { passive: true } : { capture: false } : M = p ? { passive: false, capture: true } : { capture: true }, m = [
      useEventListener(e, "mousedown", x, M),
      useEventListener(e, "touchstart", x, M)
    ];
  }), {
    isSwiping: v,
    direction: C,
    coordsStart: s,
    coordsEnd: t,
    lengthX: r,
    lengthY: f,
    stop: () => {
      m.forEach((p) => p()), P.forEach((p) => p());
    }
  };
}
function Eo(e, o) {
  const { vfmContentEl: n, modelValueLocal: l } = o, u = 0.1, i = 300, s = ref(), t = computed(() => {
    if (!(e.swipeToClose === void 0 || e.swipeToClose === "none"))
      return e.showSwipeBanner ? s.value : n.value;
  }), r = ref(0), f = ref(true);
  let y = D, d = true, c, v = false;
  const { lengthX: C, lengthY: B, direction: g, isSwiping: M } = go(t, {
    threshold: e.threshold,
    onSwipeStart(a) {
      y = useEventListener(document, "selectionchange", () => {
        var m;
        f.value = (m = window.getSelection()) == null ? void 0 : m.isCollapsed;
      }), c = new Date().getTime(), v = E(a == null ? void 0 : a.target);
    },
    onSwipe() {
      var a, m, V, p;
      if (v && f.value && g.value === e.swipeToClose) {
        if (g.value === "up") {
          const w = K(Math.abs(B.value || 0), 0, ((a = t.value) == null ? void 0 : a.offsetHeight) || 0) - (e.threshold || 0);
          r.value = w;
        } else if (g.value === "down") {
          const w = K(Math.abs(B.value || 0), 0, ((m = t.value) == null ? void 0 : m.offsetHeight) || 0) - (e.threshold || 0);
          r.value = -w;
        } else if (g.value === "right") {
          const w = K(Math.abs(C.value || 0), 0, ((V = t.value) == null ? void 0 : V.offsetWidth) || 0) - (e.threshold || 0);
          r.value = -w;
        } else if (g.value === "left") {
          const w = K(Math.abs(C.value || 0), 0, ((p = t.value) == null ? void 0 : p.offsetWidth) || 0) - (e.threshold || 0);
          r.value = w;
        }
      }
    },
    onSwipeEnd(a, m) {
      if (y(), !f.value) {
        f.value = true;
        return;
      }
      const V = new Date().getTime(), p = m === e.swipeToClose, w = (() => {
        var j, X;
        if (m === "up" || m === "down")
          return Math.abs((B == null ? void 0 : B.value) || 0) > u * (((j = t.value) == null ? void 0 : j.offsetHeight) || 0);
        if (m === "left" || m === "right")
          return Math.abs((C == null ? void 0 : C.value) || 0) > u * (((X = t.value) == null ? void 0 : X.offsetWidth) || 0);
      })(), A = V - c <= i;
      if (d && v && p && (w || A)) {
        l.value = false;
        return;
      }
      r.value = 0;
    }
  }), P = computed(() => {
    if (e.swipeToClose === "none")
      return;
    const a = (() => {
      switch (e.swipeToClose) {
        case "up":
        case "down":
          return "translateY";
        case "left":
        case "right":
          return "translateX";
      }
    })();
    return {
      class: { "vfm-bounce-back": !M.value },
      style: { transform: `${a}(${-r.value}px)` }
    };
  });
  watch(
    () => f.value,
    (a) => {
      a || (r.value = 0);
    }
  ), watch(
    () => l.value,
    (a) => {
      a && (r.value = 0);
    }
  ), watch(
    () => r.value,
    (a, m) => {
      switch (e.swipeToClose) {
        case "down":
        case "right":
          d = a < m;
          break;
        case "up":
        case "left":
          d = a > m;
          break;
      }
    }
  );
  function x(a) {
    e.preventNavigationGestures && a.preventDefault();
  }
  function E(a) {
    const m = a == null ? void 0 : a.tagName;
    if (!m || ["INPUT", "TEXTAREA"].includes(m))
      return false;
    const V = (() => {
      switch (e.swipeToClose) {
        case "up":
          return (a == null ? void 0 : a.scrollTop) + (a == null ? void 0 : a.clientHeight) === (a == null ? void 0 : a.scrollHeight);
        case "left":
          return (a == null ? void 0 : a.scrollLeft) + (a == null ? void 0 : a.clientWidth) === (a == null ? void 0 : a.scrollWidth);
        case "down":
          return (a == null ? void 0 : a.scrollTop) === 0;
        case "right":
          return (a == null ? void 0 : a.scrollLeft) === 0;
        default:
          return false;
      }
    })();
    return a === t.value ? V : V && E(a == null ? void 0 : a.parentElement);
  }
  return {
    vfmContentEl: n,
    swipeBannerEl: s,
    bindSwipe: P,
    onTouchStartSwipeBanner: x
  };
}
const ce = Symbol("vfm"), fe = Symbol("internalVfm"), Vo = /* @__PURE__ */ defineComponent({
  __name: "CoreModal",
  props: ue,
  emits: ["update:modelValue", "beforeOpen", "opened", "beforeClose", "closed", "clickOutside"],
  setup(e, { emit: o }) {
    const n = e, { modals: l, openedModals: u } = inject(ce, {
      modals: [],
      openedModals: []
    }), {
      openLastOverlay: i,
      moveToLastOpenedModals: s,
      deleteFromOpenedModals: t,
      moveToLastOpenedModalOverlays: r,
      deleteFromOpenedModalOverlays: f,
      deleteFromModals: y
    } = inject(fe, {
      openLastOverlay: D,
      moveToLastOpenedModals: D,
      deleteFromOpenedModals: D,
      moveToLastOpenedModalOverlays: D,
      deleteFromOpenedModalOverlays: D,
      deleteFromModals: D
    }), d = ref(), c = ref(), { focus: v, blur: C } = io(n, { focusEl: d }), { zIndex: B, refreshZIndex: g, resetZIndex: M } = So(n), { enableBodyScroll: P, disableBodyScroll: x } = To(n, { lockScrollEl: d }), { modelValueLocal: E } = ao(n, o), { emitEvent: a } = Mo(o);
    let m = D;
    const {
      visible: V,
      contentVisible: p,
      contentListeners: w,
      contentTransition: A,
      overlayVisible: j,
      overlayListeners: X,
      overlayTransition: He,
      enterTransition: Ne,
      leaveTransition: je
    } = so(n, {
      modelValueLocal: E,
      onEnter() {
        v(), x(), a("opened"), m("opened");
      },
      onLeave() {
        t(Ue()), M(), a("closed"), m("closed");
      }
    }), { onEsc: Ye, onMouseupRoot: $e, onMousedown: ve } = ro(n, o, { vfmRootEl: d, vfmContentEl: c, visible: V, modelValueLocal: E }), {
      swipeBannerEl: ze,
      bindSwipe: Ge,
      onTouchStartSwipeBanner: pe
    } = Eo(n, { vfmContentEl: c, modelValueLocal: E }), me = toRef(n, "hideOverlay"), F = computed(() => ({
      modalId: n.modalId,
      hideOverlay: me,
      overlayVisible: j,
      focus: v,
      toggle(b) {
        return new Promise((T) => {
          m = Oo((Xe) => T(Xe));
          const S = typeof b == "boolean" ? b : !E.value;
          E.value = S, o("update:modelValue", S);
        });
      }
    }));
    function Ue() {
      return F;
    }
    const te = computed(() => u.indexOf(F));
    watch(() => [n.zIndexFn, te.value], () => {
      V.value && g(te.value);
    }), onMounted(() => {
      l.push(F);
    }), E.value && ye(), watch(E, (b) => {
      b ? ye() : We();
    });
    async function ye() {
      a("beforeOpen"), s(F), r(F), g(te.value), i(), Ne();
    }
    function We() {
      a("beforeClose"), P(), f(F), i(), C(), je();
    }
    return onBeforeUnmount(() => {
      P(), y(F), t(F), f(F), C(), i();
    }), (b, T) => b.displayDirective !== "if" || unref(V) ? withDirectives((openBlock(), createElementBlock("div", {
      key: 0,
      ref_key: "vfmRootEl",
      ref: d,
      class: normalizeClass(["vfm vfm--fixed vfm--inset", { "vfm--prevent-none": b.background === "interactive" }]),
      style: normalizeStyle({ zIndex: unref(B) }),
      role: "dialog",
      "aria-modal": "true",
      onKeydown: T[7] || (T[7] = withKeys(() => unref(Ye)(), ["esc"])),
      onMouseup: T[8] || (T[8] = withModifiers(() => unref($e)(), ["self"])),
      onMousedown: T[9] || (T[9] = withModifiers((S) => unref(ve)(S), ["self"]))
    }, [
      unref(me) ? createCommentVNode("", true) : (openBlock(), createBlock(Transition, mergeProps({ key: 0 }, unref(He), { appear: "" }, toHandlers(unref(X))), {
        default: withCtx(() => [
          unref(j) ? (openBlock(), createElementBlock("div", {
            key: 0,
            class: normalizeClass(["vfm__overlay vfm--overlay vfm--absolute vfm--inset vfm--prevent-none", b.overlayClass]),
            style: normalizeStyle(b.overlayStyle),
            "aria-hidden": "true"
          }, null, 6)) : createCommentVNode("", true)
        ]),
        _: 1
      }, 16)),
      createVNode(Transition, mergeProps(unref(A), { appear: "" }, toHandlers(unref(w))), {
        default: withCtx(() => [
          withDirectives(createElementVNode("div", mergeProps({
            ref_key: "vfmContentEl",
            ref: c,
            class: ["vfm__content vfm--outline-none", [b.contentClass, { "vfm--prevent-auto": b.background === "interactive" }]],
            style: b.contentStyle,
            tabindex: "0"
          }, unref(Ge), {
            onMousedown: T[6] || (T[6] = () => unref(ve)())
          }), [
            renderSlot(b.$slots, "default"),
            b.showSwipeBanner ? (openBlock(), createElementBlock("div", {
              key: 0,
              ref_key: "swipeBannerEl",
              ref: ze,
              class: "vfm-swipe-banner-container",
              onTouchstart: T[2] || (T[2] = (S) => unref(pe)(S))
            }, [
              renderSlot(b.$slots, "swipe-banner", {}, () => [
                createElementVNode("div", {
                  class: "vfm-swipe-banner-back",
                  onTouchstart: T[0] || (T[0] = (S) => b.swipeToClose === "left" && S.preventDefault())
                }, null, 32),
                createElementVNode("div", {
                  class: "vfm-swipe-banner-forward",
                  onTouchstart: T[1] || (T[1] = (S) => b.swipeToClose === "right" && S.preventDefault())
                }, null, 32)
              ])
            ], 544)) : !b.showSwipeBanner && b.preventNavigationGestures ? (openBlock(), createElementBlock("div", {
              key: 1,
              class: "vfm-swipe-banner-container",
              onTouchstart: T[5] || (T[5] = (S) => unref(pe)(S))
            }, [
              createElementVNode("div", {
                class: "vfm-swipe-banner-back",
                onTouchstart: T[3] || (T[3] = (S) => b.swipeToClose === "left" && S.preventDefault())
              }, null, 32),
              createElementVNode("div", {
                class: "vfm-swipe-banner-forward",
                onTouchstart: T[4] || (T[4] = (S) => b.swipeToClose === "right" && S.preventDefault())
              }, null, 32)
            ], 32)) : createCommentVNode("", true)
          ], 16), [
            [vShow, unref(p)]
          ])
        ]),
        _: 3
      }, 16)
    ], 38)), [
      [vShow, b.displayDirective !== "show" || unref(V)]
    ]) : createCommentVNode("", true);
  }
});
const ko = {
  ...ue,
  /**
   * @description Set `null | false` to disable teleport.
   * @default `'body'`
   * @example
   * ```js
   * teleportTo: '#modals'
   * ```
   */
  teleportTo: {
    type: [String, null, Boolean, Object],
    default: "body"
  }
}, Bo = defineComponent({
  inheritAttrs: false
}), Io = /* @__PURE__ */ defineComponent({
  ...Bo,
  __name: "VueFinalModal",
  props: ko,
  emits: ["update:modelValue", "beforeOpen", "opened", "beforeClose", "closed", "clickOutside"],
  setup(e, { emit: o }) {
    const l = Fo({
      props: e,
      modalProps: ue,
      emit: o
    });
    return (u, i) => (openBlock(), createBlock(Teleport, {
      to: u.teleportTo ? u.teleportTo : void 0,
      disabled: !u.teleportTo
    }, [
      createVNode(Vo, normalizeProps(guardReactiveProps(unref(l))), {
        default: withCtx(() => [
          renderSlot(u.$slots, "default")
        ]),
        _: 3
      }, 16)
    ], 8, ["to", "disabled"]));
  }
});
function xo(e, o) {
  return Object.keys(o).reduce((n, l) => (n[l] = e[l], n), {});
}
function Do(e) {
  return e ? {
    "onUpdate:modelValue": (o) => e("update:modelValue", o),
    onBeforeClose: () => e("beforeClose"),
    onClosed: () => e("closed"),
    onBeforeOpen: () => e("beforeOpen"),
    onOpened: () => e("opened"),
    /** onClickOutside will only be emitted when clickToClose equal to `false` */
    onClickOutside: () => e("clickOutside")
  } : {};
}
function Fo(e) {
  const { props: o, modalProps: n, emit: l } = e, u = computed(() => xo(o, n)), i = Do(l), s = useAttrs();
  return computed(() => ({
    ...u.value,
    ...i,
    ...s
  }));
}

export { Vo as CoreModal, Io as VueFinalModal, ue as coreModalProps, Fo as useVfmAttrs, ko as vueFinalModalProps };
